import { create } from 'zustand';
import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { User } from '../types';
import useProjectStore from './projectStore';
import useImportJobsStore from './importJobsStore';

/**
 * B-509: wipe every per-user client store so one account's data (selected
 * project + its PII, in-progress imports) never bleeds into the next account
 * on a SHARED BROWSER. Called on logout AND when a different user signs in
 * (App.tsx auth listener). The server already isolates per user — this closes
 * the client-side display bleed that looked like a cross-tenant leak.
 */
export function clearUserScopedState(): void {
  try {
    useProjectStore.getState().reset();
  } catch {
    /* never block sign-out on a store error */
  }
  try {
    useImportJobsStore.getState().clearAll();
  } catch {
    /* never block sign-out on a store error */
  }
}

// --- AuthStore Interface ---
// Defines the shape of our authentication state and the actions available to modify it.

interface AuthStoreState {
  user: User | null;
  token: string | null;
  loading: boolean;
  // RBAC role ids the current user holds (e.g. ['admin', 'editor']).
  // Populated by App.tsx via BackendAPI.adminMe after auth.
  roles: string[];
  // True once the role-fetch in App.tsx has settled (success or failure).
  // Guards against the brief window after login where roles === [] even for
  // admins (fixes B-008). Route guards must wait for this before acting.
  rolesLoaded: boolean;
  // True when the role-fetch ERRORED (network/backend). Distinguishes "this
  // user has no roles" (pay-per customer) from "we couldn't find out" — a
  // member on a flaky connection must NOT be downgraded to the locked
  // pay-per UI with persistent side effects (forced V2, trial bootstrap).
  rolesFetchFailed: boolean;
}

interface AuthStoreActions {
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  setRoles: (roles: string[]) => void;
  setRolesLoaded: (v: boolean) => void;
  setRolesFetchFailed: (v: boolean) => void;
  logout: () => Promise<void>;
}

type AuthStore = AuthStoreState & AuthStoreActions;

// --- Zustand Store Implementation ---
// Creates the store with initial state and actions.

// Reason: In local dev with bypass enabled, set user immediately so
// ProtectedRoute never redirects to /login during first render.
const devBypass = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
const initialUser = devBypass
  ? { id: 'dev-user', email: 'dev@test.com', displayName: 'Dev User', photoURL: null }
  : null;

const useAuthStore = create<AuthStore>((set) => ({
  // --- STATE ---
  user: initialUser,
  token: null,
  loading: !devBypass, // Skip loading state when bypass is active
  roles: [],
  // Reason: In dev-bypass mode there is no role fetch, so mark as loaded
  // immediately to prevent the route guard from waiting indefinitely.
  rolesLoaded: devBypass,
  rolesFetchFailed: false,

  // --- ACTIONS ---

  setRoles: (roles) => set({ roles }),

  setRolesLoaded: (rolesLoaded) => set({ rolesLoaded }),

  setRolesFetchFailed: (rolesFetchFailed) => set({ rolesFetchFailed }),

  /**
   * Sets the current user in the store.
   * @param user - The user object from Firebase or null if logged out.
   */
  setUser: (user) => set({ user }),

  /**
   * Sets the Firebase auth token in the store.
   * This token is used for authenticating with the backend API.
   * @param token - The JWT token string or null.
   */
  setToken: (token) => set({ token }),

  /**
   * Sets the loading state.
   * This is used to show a loading indicator while the app is verifying
   * the initial authentication status with Firebase.
   * @param loading - A boolean indicating the loading status.
   */
  setLoading: (loading) => set({ loading }),

  /**
   * Logs the user out.
   * This function signs the user out of Firebase and clears the local auth state.
   */
  logout: async () => {
    try {
      await signOut(auth);
      // Clear the user and token from the store's state
      set({ user: null, token: null, roles: [], rolesLoaded: false, rolesFetchFailed: false });
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if Firebase sign-out fails, clear the local state to be safe.
      set({ user: null, token: null, roles: [], rolesLoaded: false, rolesFetchFailed: false });
    } finally {
      // B-509: always wipe the per-user stores (selected project + PII,
      // in-progress imports) so nothing bleeds into the next account on this
      // browser — even if Firebase sign-out threw.
      clearUserScopedState();
    }
  },
}));

export { useAuthStore };
export default useAuthStore;
